/**
 * Consolidated strategic plan export — v2.
 *
 * Pulls everything needed for the final deliverable from Supabase and
 * produces either a .docx (via `docx`) or a .pdf (via `jspdf`) covering all
 * 7 NVC phases for a single Caja consultancy.
 *
 * v2 adds:
 *   • Cover page with company info + industry + completion %
 *   • Trial watermark on every page
 *   • Auto-generated table of contents
 *   • Executive summary (1-line per phase)
 *   • Color-coded phase divider pages
 *   • Structured tables from market_performance / market_products /
 *     market_channels / market_competitors when section content is empty
 *   • Per-business-unit appendix
 *   • Footer with page numbers and brand
 *   • Two variants:
 *       - 'full'      → everything (default)
 *       - 'executive' → cover + TOC + summary + market tables + BU appendix
 *                       (skips the per-section markdown content)
 */
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, PageBreak,
  Table, TableRow, TableCell, WidthType, BorderStyle, ShadingType, PageNumber,
  Footer, Header,
} from 'docx'
import { supabase } from './supabase'

// ── Phase metadata (mirrors CajaDashboardPage.PHASE_META) ────────────────────
const PHASE_META = {
  0: { title: 'Modelo de Negocio',          summary: 'Contexto, propuesta de valor, segmentos, canales, ingresos, costos y recursos clave.', accent: '475569' },
  1: { title: 'Diagnóstico Estratégico',    summary: 'PESTEL, Porter, cadena de valor y matriz DOFA con cruces estratégicos.',                accent: '2563eb' },
  2: { title: 'Definición Estratégica',     summary: 'Misión, visión, valores y objetivos con KPIs por perspectiva BSC.',                     accent: '7c3aed' },
  3: { title: 'Análisis del Mercado',       summary: 'Tamaño, productos, canales, competidores y evolución histórica.',                       accent: '0891b2' },
  4: { title: 'Estrategia por UN',          summary: 'Posicionamiento, portafolio, precio, canales y comunicación por unidad de negocio.',     accent: '059669' },
  5: { title: 'Plan de Implementación',     summary: 'Iniciativas con responsables, fechas y KPIs de seguimiento.',                            accent: 'ea580c' },
  6: { title: 'Plan Corporativo',           summary: 'Narrativa estratégica que integra todas las unidades de negocio.',                       accent: '1a3f6b' },
}

// ──────────────────────────────────────────────────────────────────────────────
// Data layer
// ──────────────────────────────────────────────────────────────────────────────
async function fetchPlanData(consultancyId) {
  const [
    { data: consultancy },
    { data: profile },
    { data: ficha },
    { data: industry },
    { data: phases },
    { data: reports },
    { data: businessUnits },
    { data: marketPerformance },
    { data: marketProducts },
    { data: marketChannels },
    { data: marketCompetitors },
    { data: stakeholders },
    { data: consumerProfiles },
    { data: strategicObjectives },
  ] = await Promise.all([
    supabase.from('consultancies').select('*').eq('id', consultancyId).single(),
    supabase.from('consultancy_users').select('user_id, profiles!inner(full_name, email, subscription_status)').eq('consultancy_id', consultancyId).limit(1).maybeSingle(),
    supabase.from('project_ficha').select('*').eq('consultancy_id', consultancyId).maybeSingle(),
    supabase.from('industry_definition').select('*').eq('consultancy_id', consultancyId).maybeSingle(),
    supabase.from('phases')
      .select('id, name, phase_order, status, sections(id, name, section_order, status)')
      .eq('consultancy_id', consultancyId)
      .is('business_unit_id', null)
      .order('phase_order'),
    supabase.from('ai_reports')
      .select('entity_type, entity_id, report_type, content, updated_at')
      .eq('consultancy_id', consultancyId),
    supabase.from('business_units')
      .select('*')
      .eq('consultancy_id', consultancyId),
    safeSelect('market_performance', consultancyId),
    safeSelect('market_products',    consultancyId),
    safeSelect('market_channels',    consultancyId),
    safeSelect('market_competitors', consultancyId),
    safeSelect('stakeholders',       consultancyId),
    safeSelect('consumer_profiles',  consultancyId),
    safeSelect('strategic_objectives', consultancyId),
  ])

  // Index reports by entity for O(1) lookup. Prefer section_result > user_notes > anything else.
  const reportsByEntity = new Map()
  for (const r of reports ?? []) {
    const key   = `${r.entity_type}:${r.entity_id}`
    const score = (typ) => typ === 'section_result' ? 3 : typ === 'user_notes' ? 2 : 1
    const existing = reportsByEntity.get(key)
    if (!existing
      || score(r.report_type) > score(existing.report_type)
      || (score(r.report_type) === score(existing.report_type) && r.updated_at > existing.updated_at)) {
      reportsByEntity.set(key, r)
    }
  }

  // Sort sections inside each phase
  for (const ph of phases ?? []) {
    (ph.sections ?? []).sort((a, b) => (a.section_order ?? 0) - (b.section_order ?? 0))
  }

  // Compute progress %
  const allSections = (phases ?? []).flatMap(p => p.sections ?? [])
  const completed   = allSections.filter(s => s.status === 'completed').length
  const total       = allSections.length
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0

  return {
    consultancy,
    user:               profile?.profiles ?? null,
    ficha,
    industry,
    phases:             phases ?? [],
    reportsByEntity,
    businessUnits:      businessUnits ?? [],
    marketPerformance:  marketPerformance ?? [],
    marketProducts:     marketProducts ?? [],
    marketChannels:     marketChannels ?? [],
    marketCompetitors:  marketCompetitors ?? [],
    stakeholders:       stakeholders ?? [],
    consumerProfiles:   consumerProfiles ?? [],
    strategicObjectives: strategicObjectives ?? [],
    progressPct,
  }
}

// Wrapper that swallows missing-table or RLS errors instead of crashing the whole export.
async function safeSelect(table, consultancyId) {
  try {
    const { data, error } = await supabase.from(table).select('*').eq('consultancy_id', consultancyId)
    if (error) { console.warn(`[exportPlan] ${table} skipped:`, error.message); return { data: [] } }
    return { data: data ?? [] }
  } catch (err) {
    console.warn(`[exportPlan] ${table} threw:`, err)
    return { data: [] }
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Markdown helpers (DOCX side)
// ──────────────────────────────────────────────────────────────────────────────
function mdToParagraphs(markdown, { emptyText = '— Sección sin contenido aún —' } = {}) {
  if (!markdown || !markdown.trim()) {
    return [new Paragraph({ children: [new TextRun({ text: emptyText, italics: true, size: 20, color: '9ca3af' })] })]
  }
  const paras = []
  for (const line of markdown.split('\n')) {
    const h3 = line.match(/^### (.+)/)
    const h2 = line.match(/^## (.+)/)
    const h1 = line.match(/^# (.+)/)
    const bullet = line.match(/^[-*] (.+)/)
    const clean = line.trim()
    if (!clean) { paras.push(new Paragraph({ text: '' })); continue }
    if (h1) {
      paras.push(new Paragraph({ text: h1[1].replace(/\*\*/g, ''), heading: HeadingLevel.HEADING_3, spacing: { before: 200, after: 80 } }))
    } else if (h2) {
      paras.push(new Paragraph({ text: h2[1].replace(/\*\*/g, ''), heading: HeadingLevel.HEADING_4, spacing: { before: 160, after: 60 } }))
    } else if (h3) {
      paras.push(new Paragraph({ text: h3[1].replace(/\*\*/g, ''), heading: HeadingLevel.HEADING_5, spacing: { before: 120, after: 40 } }))
    } else if (bullet) {
      paras.push(new Paragraph({ children: [new TextRun({ text: bullet[1].replace(/\*\*/g, ''), size: 22 })], bullet: { level: 0 }, spacing: { after: 40 } }))
    } else {
      const parts = line.split(/(\*\*[^*]+\*\*)/)
      const runs = parts.map(p =>
        p.startsWith('**') && p.endsWith('**')
          ? new TextRun({ text: p.slice(2, -2), bold: true, size: 22 })
          : new TextRun({ text: p, size: 22 })
      )
      paras.push(new Paragraph({ children: runs, spacing: { after: 60 } }))
    }
  }
  return paras
}

// ── DOCX table helpers ───────────────────────────────────────────────────────
function dataTable({ headers, rows, columnWidths }) {
  const totalWidth = columnWidths.reduce((a, b) => a + b, 0)
  const border = { style: BorderStyle.SINGLE, size: 4, color: 'd1d5db' }
  const cellBorders = { top: border, bottom: border, left: border, right: border }

  return new Table({
    width: { size: totalWidth, type: WidthType.DXA },
    columnWidths,
    rows: [
      new TableRow({
        tableHeader: true,
        children: headers.map((h, i) => new TableCell({
          borders: cellBorders,
          width: { size: columnWidths[i], type: WidthType.DXA },
          shading: { fill: 'f3f4f6', type: ShadingType.CLEAR },
          margins: { top: 100, bottom: 100, left: 120, right: 120 },
          children: [new Paragraph({
            children: [new TextRun({ text: String(h ?? ''), bold: true, size: 20, color: '111827' })],
          })],
        })),
      }),
      ...rows.map(row => new TableRow({
        children: row.map((cell, i) => new TableCell({
          borders: cellBorders,
          width: { size: columnWidths[i], type: WidthType.DXA },
          margins: { top: 80, bottom: 80, left: 120, right: 120 },
          children: [new Paragraph({
            children: [new TextRun({ text: String(cell ?? '—'), size: 20 })],
          })],
        })),
      })),
    ],
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// DOCX builder
// ──────────────────────────────────────────────────────────────────────────────
function buildDocx(data, { variant = 'full', isTrial = false } = {}) {
  const date = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
  const company = data.consultancy?.client_company ?? 'Empresa'
  const industry = data.consultancy?.industry ?? ''
  const children = []

  // ── Cover ──
  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'PLAN ESTRATÉGICO', bold: true, size: 48, color: '1a3f6b' })],
      alignment: AlignmentType.CENTER, spacing: { before: 1200, after: 200 },
    }),
    new Paragraph({
      children: [new TextRun({ text: company.toUpperCase(), bold: true, size: 36, color: '111827' })],
      alignment: AlignmentType.CENTER, spacing: { after: 80 },
    }),
    industry && new Paragraph({
      children: [new TextRun({ text: industry, size: 22, color: '6b7280', italics: true })],
      alignment: AlignmentType.CENTER, spacing: { after: 100 },
    }),
    new Paragraph({
      children: [new TextRun({ text: variant === 'executive' ? 'RESUMEN EJECUTIVO' : 'PLAN COMPLETO', bold: true, size: 18, color: '1a3f6b' })],
      alignment: AlignmentType.CENTER, spacing: { after: 600 },
    }),
    new Paragraph({
      children: [new TextRun({ text: `${data.progressPct}% completado`, size: 18, color: '059669' })],
      alignment: AlignmentType.CENTER, spacing: { after: 800 },
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Construido con la metodología de', size: 18, color: '9ca3af' })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({
      children: [new TextRun({ text: 'NEXT VALLEY CONSULTANTS', bold: true, size: 22, color: '1a3f6b' })],
      alignment: AlignmentType.CENTER, spacing: { after: 80 },
    }),
    new Paragraph({
      children: [new TextRun({ text: `Generado el ${date}`, size: 18, color: '9ca3af' })],
      alignment: AlignmentType.CENTER,
    }),
    new Paragraph({ children: [new PageBreak()] }),
  )

  // ── Trial banner (in-content, also show at top of page 2) ──
  if (isTrial) {
    children.push(
      new Paragraph({
        children: [new TextRun({
          text: '⚠ BORRADOR — VERSIÓN TRIAL · activa tu suscripción para retirar esta marca',
          bold: true, size: 18, color: 'b45309', italics: true,
        })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 240 },
      }),
    )
  }

  // ── Project info table ──
  if (data.ficha || data.consultancy) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: 'Ficha del proyecto', bold: true })],
        spacing: { before: 120, after: 200 },
      }),
      dataTable({
        columnWidths: [3000, 6360],
        headers: ['Campo', 'Valor'],
        rows: [
          ['Empresa', company],
          ['Industria', industry || '—'],
          ['Tamaño', data.ficha?.tamano_empresa ?? '—'],
          ['País', data.consultancy?.country ?? '—'],
          ['Inicio', formatDate(data.ficha?.fecha_inicio)],
          ['Cliente principal', data.user?.full_name ?? '—'],
          ['Fecha del documento', date],
          ['Avance global', `${data.progressPct}%`],
        ],
      }),
    )
    if (data.ficha?.descripcion_negocio) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'Descripción del negocio', bold: true, size: 22 })],
          spacing: { before: 240, after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: data.ficha.descripcion_negocio, size: 22 })],
          spacing: { after: 120 },
        }),
      )
    }
    children.push(new Paragraph({ children: [new PageBreak()] }))
  }

  // ── Table of contents (manual list, not docx TOC field) ──
  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: 'Contenido', bold: true })],
      spacing: { before: 120, after: 200 },
    }),
  )
  for (const phase of data.phases) {
    children.push(new Paragraph({
      children: [
        new TextRun({ text: `Etapa ${phase.phase_order}.  `, bold: true, size: 22, color: '1a3f6b' }),
        new TextRun({ text: phase.name, size: 22 }),
      ],
      spacing: { after: 60 },
    }))
    if (variant === 'full') {
      for (const section of phase.sections ?? []) {
        children.push(new Paragraph({
          children: [new TextRun({ text: `        ${section.name}`, size: 20, color: '6b7280' })],
          spacing: { after: 30 },
        }))
      }
    }
  }
  children.push(new Paragraph({ children: [new PageBreak()] }))

  // ── Executive summary ──
  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: 'Resumen ejecutivo', bold: true })],
      spacing: { before: 120, after: 200 },
    }),
  )
  for (const phase of data.phases) {
    const meta = PHASE_META[phase.phase_order] ?? {}
    const sections = phase.sections ?? []
    const completed = sections.filter(s => s.status === 'completed').length
    const phaseReport = data.reportsByEntity.get(`phase:${phase.id}`)
    const summaryText = phaseReport?.content
      ? truncate(stripMarkdown(phaseReport.content), 320)
      : meta.summary ?? ''
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `Etapa ${phase.phase_order}. `, bold: true, size: 22, color: meta.accent ?? '1a3f6b' }),
          new TextRun({ text: phase.name, bold: true, size: 22 }),
          new TextRun({ text: `   (${completed}/${sections.length} secciones)`, size: 18, color: '9ca3af' }),
        ],
        spacing: { before: 160, after: 40 },
      }),
      new Paragraph({
        children: [new TextRun({ text: summaryText, size: 21, color: '374151' })],
        spacing: { after: 80 },
      }),
    )
  }
  children.push(new Paragraph({ children: [new PageBreak()] }))

  // ── Per-phase content (skipped for executive) ──
  if (variant === 'full') {
    for (const phase of data.phases) {
      const meta = PHASE_META[phase.phase_order] ?? {}
      // Phase divider
      children.push(
        new Paragraph({
          children: [new TextRun({ text: `ETAPA ${phase.phase_order}`, bold: true, size: 24, color: meta.accent ?? '1a3f6b' })],
          alignment: AlignmentType.CENTER, spacing: { before: 800, after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: phase.name.toUpperCase(), bold: true, size: 36, color: '111827' })],
          alignment: AlignmentType.CENTER, spacing: { after: 200 },
        }),
        new Paragraph({
          children: [new TextRun({ text: meta.summary ?? '', size: 22, color: '6b7280', italics: true })],
          alignment: AlignmentType.CENTER, spacing: { after: 600 },
        }),
        new Paragraph({ children: [new PageBreak()] }),
      )

      if (!phase.sections || phase.sections.length === 0) {
        children.push(new Paragraph({
          children: [new TextRun({ text: 'Esta etapa aún no tiene secciones.', italics: true, color: '9ca3af', size: 22 })],
          spacing: { after: 200 },
        }))
        continue
      }

      for (const section of phase.sections) {
        children.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [new TextRun({ text: section.name, bold: true })],
            spacing: { before: 240, after: 100 },
          }),
        )
        const report = data.reportsByEntity.get(`section:${section.id}`)
        children.push(...mdToParagraphs(report?.content))
      }

      // Etapa 3 enrichment with structured market data
      if (phase.phase_order === 3) {
        const tables = buildMarketTablesDocx(data)
        if (tables.length) {
          children.push(
            new Paragraph({
              heading: HeadingLevel.HEADING_2,
              children: [new TextRun({ text: 'Datos de mercado', bold: true })],
              spacing: { before: 360, after: 120 },
            }),
            ...tables,
          )
        }
      }

      children.push(new Paragraph({ children: [new PageBreak()] }))
    }
  }

  // ── Strategic objectives appendix ──
  if (data.strategicObjectives.length > 0) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: 'Anexo: Objetivos Estratégicos', bold: true })],
        spacing: { before: 320, after: 200 },
      }),
      dataTable({
        columnWidths: [2400, 5000, 1980],
        headers: ['Perspectiva', 'Objetivo', 'Meta'],
        rows: data.strategicObjectives.map(o => [
          o.perspective ?? o.bsc_perspective ?? '—',
          o.name ?? o.title ?? o.description ?? '—',
          o.target ?? o.goal ?? o.kpi ?? '—',
        ]),
      }),
      new Paragraph({ children: [new PageBreak()] }),
    )
  }

  // ── Business units appendix ──
  if (data.businessUnits.length > 0) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: 'Anexo: Unidades de Negocio', bold: true })],
        spacing: { before: 320, after: 200 },
      }),
    )
    for (const bu of data.businessUnits) {
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          children: [new TextRun({ text: bu.name, bold: true })],
          spacing: { before: 200, after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: bu.description ?? '—', size: 22 })],
          spacing: { after: 120 },
        }),
      )
    }
  }

  // ── Footer with page numbers ──
  children.push(
    new Paragraph({
      children: [new TextRun({
        text: `Documento generado el ${date} con la plataforma de Next Valley Consultants — www.nvc-seguimiento.com`,
        size: 16, color: '9ca3af', italics: true,
      })],
      alignment: AlignmentType.CENTER, spacing: { before: 600 },
    }),
  )

  return new Document({
    sections: [{
      properties: {},
      headers: isTrial ? {
        default: new Header({
          children: [new Paragraph({
            children: [new TextRun({ text: 'BORRADOR · TRIAL', bold: true, color: 'b45309', size: 16 })],
            alignment: AlignmentType.RIGHT,
          })],
        }),
      } : undefined,
      footers: {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({ text: 'Next Valley Consultants  ·  Página ', size: 16, color: '9ca3af' }),
              new TextRun({ children: [PageNumber.CURRENT], size: 16, color: '9ca3af' }),
              new TextRun({ text: ' de ', size: 16, color: '9ca3af' }),
              new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: '9ca3af' }),
            ],
          })],
        }),
      },
      children,
    }],
    styles: { default: { document: { run: { font: 'Calibri', size: 22 } } } },
  })
}

function buildMarketTablesDocx(data) {
  const out = []

  if (data.marketPerformance.length > 0) {
    out.push(
      new Paragraph({
        children: [new TextRun({ text: 'Desempeño del mercado', bold: true, size: 22 })],
        spacing: { before: 200, after: 80 },
      }),
      dataTable({
        columnWidths: [1500, 2620, 2620, 2620],
        headers: ['Año', 'Tamaño', 'Volumen', 'Crecimiento'],
        rows: data.marketPerformance.slice(0, 8).map(r => [
          r.year ?? r.data_year ?? '—',
          formatNumber(r.size ?? r.market_size ?? r.value),
          formatNumber(r.volume),
          formatPct(r.growth_rate ?? r.cagr ?? r.growth),
        ]),
      }),
    )
  }

  if (data.marketProducts.length > 0) {
    out.push(
      new Paragraph({
        children: [new TextRun({ text: 'Portafolio del mercado', bold: true, size: 22 })],
        spacing: { before: 240, after: 80 },
      }),
      dataTable({
        columnWidths: [3500, 1960, 1950, 1950],
        headers: ['Producto / Categoría', 'Ventas', 'Volumen', 'CAGR'],
        rows: data.marketProducts.slice(0, 12).map(r => [
          r.name ?? r.product_name ?? r.category ?? '—',
          formatNumber(r.sales ?? r.value),
          formatNumber(r.volume),
          formatPct(r.cagr ?? r.growth_rate),
        ]),
      }),
    )
  }

  if (data.marketCompetitors.length > 0) {
    out.push(
      new Paragraph({
        children: [new TextRun({ text: 'Competidores', bold: true, size: 22 })],
        spacing: { before: 240, after: 80 },
      }),
      dataTable({
        columnWidths: [4000, 2680, 2680],
        headers: ['Competidor', 'Participación', 'Notas'],
        rows: data.marketCompetitors.slice(0, 12).map(r => [
          r.name ?? r.competitor_name ?? '—',
          formatPct(r.share ?? r.market_share),
          truncate(r.notes ?? r.description ?? '', 80),
        ]),
      }),
    )
  }

  return out
}

// ──────────────────────────────────────────────────────────────────────────────
// PDF builder
// ──────────────────────────────────────────────────────────────────────────────
async function buildPdf(data, { variant = 'full', isTrial = false } = {}) {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 20
  const maxW = pageW - margin * 2
  const company = data.consultancy?.client_company ?? 'Empresa'
  const industry = data.consultancy?.industry ?? ''
  const date = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })

  // ── Cover page ──
  doc.setFillColor(26, 63, 107)   // brand-700
  doc.rect(0, 0, pageW, pageH, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(28)
  doc.text('PLAN ESTRATÉGICO', pageW / 2, 92, { align: 'center' })
  doc.setFontSize(20)
  doc.text(company.toUpperCase(), pageW / 2, 108, { align: 'center' })
  if (industry) {
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(11)
    doc.setTextColor(200, 220, 240)
    doc.text(industry, pageW / 2, 117, { align: 'center' })
  }
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(245, 158, 11)   // amber accent
  doc.text(variant === 'executive' ? 'RESUMEN EJECUTIVO' : 'PLAN COMPLETO', pageW / 2, 132, { align: 'center' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.setTextColor(180, 200, 220)
  doc.text(`${data.progressPct}% completado`, pageW / 2, 148, { align: 'center' })

  doc.setFontSize(9)
  doc.setTextColor(160, 180, 210)
  doc.text('Construido con la metodología de', pageW / 2, 230, { align: 'center' })
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(255, 255, 255)
  doc.text('NEXT VALLEY CONSULTANTS', pageW / 2, 237, { align: 'center' })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(160, 180, 210)
  doc.text(`Generado el ${date}`, pageW / 2, 245, { align: 'center' })

  // ── Helpers ──
  const ctx = { doc, pageW, pageH, margin, maxW, isTrial, brand: '#1a3f6b' }

  function startPage(title, accentHex = '1a3f6b') {
    doc.addPage()
    drawHeaderBar(ctx, title, accentHex)
    drawWatermark(ctx)
    return 46
  }

  // ── Project info ──
  if (data.ficha || data.consultancy) {
    let y = startPage('Ficha del proyecto')
    const rows = [
      ['Empresa', company],
      ['Industria', industry || '—'],
      ['Tamaño', data.ficha?.tamano_empresa ?? '—'],
      ['País', data.consultancy?.country ?? '—'],
      ['Inicio', formatDate(data.ficha?.fecha_inicio)],
      ['Cliente principal', data.user?.full_name ?? '—'],
      ['Fecha del documento', date],
      ['Avance global', `${data.progressPct}%`],
    ]
    y = drawKeyValueTable(ctx, rows, y)
    if (data.ficha?.descripcion_negocio) {
      y = ensureSpace(ctx, y, 30)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor(17, 24, 39)
      doc.text('Descripción del negocio', margin, y)
      y += 6
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      const lines = doc.splitTextToSize(data.ficha.descripcion_negocio, maxW)
      for (const line of lines) {
        y = ensureSpace(ctx, y, 6)
        doc.text(line, margin, y); y += 5.5
      }
    }
  }

  // ── Table of contents ──
  let y = startPage('Contenido')
  doc.setFontSize(10)
  for (const phase of data.phases) {
    y = ensureSpace(ctx, y, 8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(26, 63, 107)
    doc.text(`Etapa ${phase.phase_order}. ${phase.name}`, margin, y)
    y += 6
    if (variant === 'full') {
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(107, 114, 128)
      doc.setFontSize(9)
      for (const section of phase.sections ?? []) {
        y = ensureSpace(ctx, y, 5.5)
        doc.text(`        ${section.name}`, margin, y); y += 5
      }
      doc.setFontSize(10)
    }
    y += 2
  }

  // ── Executive summary ──
  y = startPage('Resumen ejecutivo')
  for (const phase of data.phases) {
    const meta = PHASE_META[phase.phase_order] ?? {}
    const sections = phase.sections ?? []
    const completed = sections.filter(s => s.status === 'completed').length
    const phaseReport = data.reportsByEntity.get(`phase:${phase.id}`)
    const summaryText = phaseReport?.content
      ? truncate(stripMarkdown(phaseReport.content), 280)
      : meta.summary ?? ''

    y = ensureSpace(ctx, y, 18)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(...hexToRgb(meta.accent ?? '1a3f6b'))
    doc.text(`Etapa ${phase.phase_order}. ${phase.name}`, margin, y)
    doc.setFontSize(8)
    doc.setTextColor(156, 163, 175)
    doc.text(`${completed}/${sections.length} secciones`, pageW - margin, y, { align: 'right' })
    y += 6
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(55, 65, 81)
    const lines = doc.splitTextToSize(summaryText, maxW)
    for (const line of lines) {
      y = ensureSpace(ctx, y, 5.5)
      doc.text(line, margin, y); y += 5
    }
    y += 4
  }

  // ── Per-phase content (only in 'full') ──
  if (variant === 'full') {
    for (const phase of data.phases) {
      const meta = PHASE_META[phase.phase_order] ?? {}
      // Phase cover
      doc.addPage()
      doc.setFillColor(...hexToRgb(meta.accent ?? '1a3f6b'))
      doc.rect(0, 0, pageW, pageH, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text(`ETAPA ${phase.phase_order}`, pageW / 2, 90, { align: 'center' })
      doc.setFontSize(28)
      doc.text(phase.name.toUpperCase(), pageW / 2, 110, { align: 'center' })
      doc.setFontSize(11)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(220, 230, 245)
      const introLines = doc.splitTextToSize(meta.summary ?? '', maxW)
      let introY = 130
      for (const l of introLines) { doc.text(l, pageW / 2, introY, { align: 'center' }); introY += 6 }
      drawWatermark(ctx)

      let py = startPage(`Etapa ${phase.phase_order}. ${phase.name}`, meta.accent)

      for (const section of phase.sections ?? []) {
        py = ensureSpace(ctx, py, 12)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(12)
        doc.setTextColor(...hexToRgb(meta.accent ?? '1a3f6b'))
        doc.text(section.name, margin, py); py += 7
        const report = data.reportsByEntity.get(`section:${section.id}`)
        py = drawMarkdown(ctx, report?.content, py)
        py += 4
      }

      if (phase.phase_order === 3) {
        py = drawMarketSection(ctx, data, py)
      }
    }
  }

  // ── Strategic objectives appendix ──
  if (data.strategicObjectives.length > 0) {
    let y2 = startPage('Anexo: Objetivos Estratégicos')
    drawDataTable(ctx, {
      headers: ['Perspectiva', 'Objetivo', 'Meta'],
      colWidths: [40, 90, 40],
      rows: data.strategicObjectives.map(o => [
        o.perspective ?? o.bsc_perspective ?? '—',
        o.name ?? o.title ?? o.description ?? '—',
        o.target ?? o.goal ?? o.kpi ?? '—',
      ]),
    }, y2)
  }

  // ── Business units appendix ──
  if (data.businessUnits.length > 0) {
    let y3 = startPage('Anexo: Unidades de Negocio')
    for (const bu of data.businessUnits) {
      y3 = ensureSpace(ctx, y3, 14)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(12)
      doc.setTextColor(26, 63, 107)
      doc.text(bu.name, margin, y3); y3 += 6
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.setTextColor(55, 65, 81)
      const lines = doc.splitTextToSize(bu.description ?? '—', maxW)
      for (const line of lines) {
        y3 = ensureSpace(ctx, y3, 5.5)
        doc.text(line, margin, y3); y3 += 5
      }
      y3 += 4
    }
  }

  // ── Footer page numbers (skip cover) ──
  const totalPages = doc.internal.getNumberOfPages()
  for (let i = 2; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(150)
    doc.text(`Next Valley Consultants  ·  Página ${i - 1} de ${totalPages - 1}`, pageW / 2, pageH - 8, { align: 'center' })
  }

  return doc
}

// ── PDF helpers ──────────────────────────────────────────────────────────────
function ensureSpace({ doc, pageH }, y, needed) {
  if (y + needed > pageH - 18) { doc.addPage(); return 25 }
  return y
}

function drawHeaderBar({ doc, pageW, margin }, title, accentHex) {
  const [r, g, b] = hexToRgb(accentHex)
  doc.setFillColor(r, g, b)
  doc.rect(0, 0, pageW, 32, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.text('NEXT VALLEY CONSULTANTS', margin, 11)
  doc.setFontSize(13)
  doc.setFont('helvetica', 'bold')
  doc.text(title, margin, 22)
  doc.setTextColor(30, 30, 30)
}

function drawWatermark({ doc, pageW, pageH, isTrial }) {
  if (!isTrial) return
  // Diagonal "BORRADOR · TRIAL" centered
  doc.saveGraphicsState()
  // jspdf <4 doesn't support GState alpha cross-platform. Use a faint color instead.
  doc.setTextColor(220, 220, 220)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(60)
  doc.text('BORRADOR · TRIAL', pageW / 2, pageH / 2, {
    align: 'center', angle: 30,
  })
  doc.restoreGraphicsState()
  // Reset to default
  doc.setTextColor(30, 30, 30)
  doc.setFontSize(10)
}

function drawMarkdown(ctx, markdown, y) {
  const { doc, margin, maxW } = ctx
  if (!markdown || !markdown.trim()) {
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(9)
    doc.setTextColor(156, 163, 175)
    doc.text('— Sección sin contenido aún —', margin, y)
    doc.setTextColor(30, 30, 30)
    return y + 6
  }
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(55, 65, 81)
  for (const line of markdown.split('\n')) {
    const isH = line.match(/^#{1,3}\s+(.+)/)
    const isBullet = line.match(/^[-*]\s+(.+)/)
    let text = line
    if (isH) text = isH[1]
    if (isBullet) text = '• ' + isBullet[1]
    text = text.replace(/\*\*/g, '')
    if (!text.trim()) { y += 3; continue }
    const lines = doc.splitTextToSize(text, maxW)
    for (const l of lines) {
      y = ensureSpace(ctx, y, 5.5)
      doc.setFont('helvetica', isH ? 'bold' : 'normal')
      doc.setFontSize(isH ? 11 : 10)
      doc.text(l, margin, y); y += isH ? 6 : 5
    }
  }
  return y
}

function drawKeyValueTable(ctx, rows, startY) {
  const { doc, margin, maxW } = ctx
  let y = startY
  const colKey = 50
  const colVal = maxW - colKey
  for (const [k, v] of rows) {
    y = ensureSpace(ctx, y, 8)
    doc.setFillColor(243, 244, 246)
    doc.rect(margin, y - 4, colKey, 7, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(31, 41, 55)
    doc.text(k, margin + 3, y + 1.5)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(55, 65, 81)
    const valLines = doc.splitTextToSize(String(v ?? '—'), colVal - 6)
    let vy = y + 1.5
    for (const l of valLines) {
      doc.text(l, margin + colKey + 3, vy); vy += 5
    }
    y += Math.max(7, valLines.length * 5 + 2)
  }
  return y + 4
}

function drawDataTable(ctx, { headers, colWidths, rows }, startY) {
  const { doc, margin } = ctx
  let y = startY
  // Header row
  y = ensureSpace(ctx, y, 9)
  doc.setFillColor(243, 244, 246)
  doc.rect(margin, y - 4, colWidths.reduce((a, b) => a + b, 0), 7, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(17, 24, 39)
  let cx = margin + 2
  headers.forEach((h, i) => { doc.text(h, cx, y + 1); cx += colWidths[i] })
  y += 8
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(55, 65, 81)
  for (const row of rows) {
    y = ensureSpace(ctx, y, 8)
    cx = margin + 2
    row.forEach((cell, i) => {
      const cellLines = doc.splitTextToSize(String(cell ?? '—'), colWidths[i] - 4)
      doc.text(cellLines[0] ?? '', cx, y + 1)
      cx += colWidths[i]
    })
    y += 6
  }
  return y + 4
}

function drawMarketSection(ctx, data, startY) {
  let y = startY
  const { doc, margin } = ctx
  if (data.marketPerformance.length > 0) {
    y = ensureSpace(ctx, y, 14)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.setTextColor(26, 63, 107)
    doc.text('Desempeño del mercado', margin, y); y += 6
    y = drawDataTable(ctx, {
      headers: ['Año', 'Tamaño', 'Volumen', 'Crecimiento'],
      colWidths: [20, 50, 50, 50],
      rows: data.marketPerformance.slice(0, 8).map(r => [
        r.year ?? r.data_year ?? '—',
        formatNumber(r.size ?? r.market_size ?? r.value),
        formatNumber(r.volume),
        formatPct(r.growth_rate ?? r.cagr ?? r.growth),
      ]),
    }, y)
  }
  if (data.marketProducts.length > 0) {
    y = ensureSpace(ctx, y, 14)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.setTextColor(26, 63, 107)
    doc.text('Portafolio del mercado', margin, y); y += 6
    y = drawDataTable(ctx, {
      headers: ['Producto / Categoría', 'Ventas', 'Volumen', 'CAGR'],
      colWidths: [60, 35, 35, 40],
      rows: data.marketProducts.slice(0, 12).map(r => [
        r.name ?? r.product_name ?? r.category ?? '—',
        formatNumber(r.sales ?? r.value),
        formatNumber(r.volume),
        formatPct(r.cagr ?? r.growth_rate),
      ]),
    }, y)
  }
  if (data.marketCompetitors.length > 0) {
    y = ensureSpace(ctx, y, 14)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.setTextColor(26, 63, 107)
    doc.text('Competidores', margin, y); y += 6
    y = drawDataTable(ctx, {
      headers: ['Competidor', 'Participación', 'Notas'],
      colWidths: [70, 40, 60],
      rows: data.marketCompetitors.slice(0, 12).map(r => [
        r.name ?? r.competitor_name ?? '—',
        formatPct(r.share ?? r.market_share),
        truncate(r.notes ?? r.description ?? '', 50),
      ]),
    }, y)
  }
  return y
}

// ──────────────────────────────────────────────────────────────────────────────
// Format helpers
// ──────────────────────────────────────────────────────────────────────────────
function formatNumber(v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('es-CO', { maximumFractionDigits: 1 })
}

function formatPct(v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  // Heuristic: values > 1 are already percentages (e.g. 12.5), values <= 1 are fractions.
  return (n > 1 ? n : n * 100).toLocaleString('es-CO', { maximumFractionDigits: 1 }) + '%'
}

function formatDate(v) {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
}

function stripMarkdown(s) {
  return (s ?? '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/[*_`]/g, '')
    .replace(/\n+/g, ' ')
    .trim()
}

function truncate(s, n) {
  s = s ?? ''
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + '…'
}

function hexToRgb(hex) {
  const h = (hex ?? '1a3f6b').replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

// ──────────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────────
function downloadBlob(blob, filename) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

function safeFilename(s) {
  return (s || 'plan-estrategico').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function suffixForVariant(variant) {
  return variant === 'executive' ? '-resumen-ejecutivo' : ''
}

async function getPlanContext(consultancyId) {
  const data = await fetchPlanData(consultancyId)
  if (!data.consultancy) throw new Error('No se encontró el proyecto.')
  const isTrial = !data.user || !data.user.subscription_status
    || data.user.subscription_status === 'trial'
    || data.user.subscription_status === 'expired'
  return { data, isTrial }
}

export async function exportStrategicPlanDocx(consultancyId, variant = 'full') {
  const { data, isTrial } = await getPlanContext(consultancyId)
  const doc = buildDocx(data, { variant, isTrial })
  const blob = await Packer.toBlob(doc)
  downloadBlob(blob, `plan-estrategico-${safeFilename(data.consultancy.client_company)}${suffixForVariant(variant)}.docx`)
}

export async function exportStrategicPlanPdf(consultancyId, variant = 'full') {
  const { data, isTrial } = await getPlanContext(consultancyId)
  const doc = await buildPdf(data, { variant, isTrial })
  doc.save(`plan-estrategico-${safeFilename(data.consultancy.client_company)}${suffixForVariant(variant)}.pdf`)
}
